import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactCountryFlag from "react-country-flag";

import Modal from "../components/ui/Modal";
import Badge from "../components/ui/Badge";
import Icon from "../components/ui/Icon";
import formatMessageTime from "./formatMessageTime";
import HighlightMatch from "./HighlightMatch";
import Money from "../components/ui/Money";
import pricesOf from "./pricesOf";
import whatsappChatLink from "./whatsappChatLink";
import { useWhatsappTarget } from "./whatsappTarget";
import { PRICES_URL } from "./useTradeFeed";

/**
 * The whole message, full size — the destination of every card's "See more".
 *
 * Built on the shared Modal (M31) rather than a bespoke overlay so it inherits
 * the focus trap, Escape, scroll lock and stacking the rest of the app has. The
 * `trade-feed-modal` class only widens it: the point of this popup is a photo
 * of a watch and a long price list, neither of which fits the 640px dialog.
 *
 * `variant="auto"` and not "dialog": on a phone that resolves to the bottom
 * SHEET, which is the only shape that gets `max-height: 92dvh`,
 * `env(safe-area-inset-bottom)` and swipe-to-dismiss. Pinning it to "dialog"
 * gave phones a centred box with none of that — and this page is read on a
 * phone as much as on a laptop.
 */
/**
 * @param {node} extra  Optional block rendered above the message text. Core
 *   List passes the trade price it extracted from this message, so the popup
 *   answers "what did we read out of this" and not only "what was said". The
 *   feed passes nothing.
 */
/**
 * Every price the card summarises, one line each with its reference: the card
 * shows the first and "+N", the popup shows them all (Alex, 2026-10-20). For
 * extracted prices the server sends only the first few, so the rest are
 * counted, not listed.
 */
/**
 * The message's WHOLE price list (Alex, 2026-10-23: scroll through all of them
 * instead of "+91 more"). A card carries only its first few prices, so when
 * there are more the popup asks the server for this one message's full list.
 * The prices already on the card show at once; the rest arrive behind them.
 * On a failure the card's own prices stay, with the count of the rest.
 */
const useFullPrices = (item, query) => {
  const [full, setFull] = useState(null);
  const [failed, setFailed] = useState(false);
  const id = item?.id;
  const needsMore = Array.isArray(item?.prices) && (item.price_count ?? 0) > item.prices.length;

  useEffect(() => {
    setFull(null);
    setFailed(false);
    if (!id || !needsMore) return undefined;
    let cancelled = false;
    axios
      .post(PRICES_URL, { ids: [id], query: query.trim() || undefined, all: true })
      .then((response) => {
        const fresh = response?.data?.items?.[id];
        if (!cancelled && fresh?.prices) setFull({ ...item, ...fresh });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // Once per message and search: the item object changes on every minute's
    // re-check, and that must not re-fetch a list the reader is scrolling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, needsMore, query]);

  return { shown: full ?? item, loading: needsMore && !full && !failed };
};

const MessagePrices = ({ item, query = "" }) => {
  const { shown, loading } = useFullPrices(item, query);
  const { kind, prices, count, approvedCount, unapprovedCount } = pricesOf(shown);
  if (kind === "none") return null;
  const matches = prices.filter((p) => p.match).length;

  return (
    // Last in the popup, under a divider (Alex, 2026-10-23), as a table with
    // a header row, so reference, watch, dial, year and both prices line up.
    <section className="trade-feed-modal__prices" aria-label="Prices">
      <div className="trade-feed-modal__prices-head">
        <h3 className="trade-feed-modal__prices-title">{count > 1 ? "Prices" : "Price"}</h3>
        <span className="trade-feed-modal__price-counts">
          {[approvedCount ? `${approvedCount} approved` : null, unapprovedCount ? `${unapprovedCount} unapproved` : null]
            .filter(Boolean)
            .join(" · ")}
        </span>
        {/* The server sorts the searched reference's prices first and marks
            them, so the answer to "what is 116503 at" is the top of the table. */}
        {matches > 0 && query.trim() && (
          <span className="trade-feed-modal__price-matches">
            {matches} match {query.trim()}
          </span>
        )}
      </div>

      {/* A fixed window that scrolls; the column headers stay put. */}
      <div className="trade-feed-modal__price-scroll">
        <table className="trade-feed-modal__price-table">
          <thead>
            <tr>
              <th scope="col">Status</th>
              <th scope="col">Reference</th>
              <th scope="col">Watch</th>
              <th scope="col">Dial</th>
              <th scope="col">Year</th>
              <th scope="col" className="is-num">Price</th>
              <th scope="col" className="is-num">≈ GBP</th>
            </tr>
          </thead>
          <tbody>
            {prices.map((p, i) => (
              <tr key={i} className={p.match ? "is-match" : undefined}>
                <td>
                  <span className={`trade-feed-modal__state${p.approved ? " is-approved" : ""}`}>
                    {p.approved ? "Approved" : "Unapproved"}
                  </span>
                </td>
                <td>
                  {p.reference ? (
                    p.product_id ? (
                      <a
                        className="trade-feed-modal__price-ref"
                        href={`/view-product/${p.product_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <HighlightMatch text={p.reference} query={query} />
                      </a>
                    ) : (
                      <span className="trade-feed-modal__price-ref">
                        <HighlightMatch text={p.reference} query={query} />
                      </span>
                    )
                  ) : (
                    <span className="trade-feed-modal__price-ref is-unknown">No reference</span>
                  )}
                </td>
                <td>{p.name || <span className="is-empty">—</span>}</td>
                <td>{p.dial || <span className="is-empty">—</span>}</td>
                <td>{p.year || <span className="is-empty">—</span>}</td>
                <td className="is-num">
                  <strong>
                    <Money value={p.price} currency={p.currency} decimals={0} />
                  </strong>
                </td>
                <td className="is-num">
                  {p.currency === "gbp" ? (
                    <span className="is-empty">—</span>
                  ) : p.price_gbp != null ? (
                    <Money value={p.price_gbp} currency="gbp" decimals={0} tone="muted" />
                  ) : (
                    <span className="is-empty">No rate</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {count > prices.length && (
        <p className="trade-feed-modal__price-more" role="status">
          {loading ? `Loading ${count - prices.length} more…` : `+${count - prices.length} more in this message`}
        </p>
      )}
    </section>
  );
};

const TradeFeedModal = ({ item, onClose, query = "", extra = null }) => {
  const open = Boolean(item);
  const time = item ? formatMessageTime(item.timestamp) : { absolute: "", relative: "" };
  const waTarget = useWhatsappTarget();
  const waLink = item ? whatsappChatLink(item, waTarget) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="auto"
      size="lg"
      className="trade-feed-modal"
      title={item?.sender_name || "Message"}
      description={item ? [item.group_name || "Direct message", time.absolute].filter(Boolean).join(" · ") : undefined}
    >
      {item ? (
        <div className="trade-feed-modal__body">
          {item.media_kind === "image" && item.media_url ? (
            <figure className="trade-feed-modal__media">
              <img src={item.media_url} alt={item.file_name || "WhatsApp attachment"} />
            </figure>
          ) : item.media_url ? (
            // Not previewed inline — opened in a tab, which is also the only
            // way a video or a PDF is useful here.
            <a className="trade-feed-modal__attachment" href={item.media_url} target="_blank" rel="noopener noreferrer">
              <Icon name="Paperclip" size="sm" />
              {item.file_name || "Open attachment"}
            </a>
          ) : null}

          {extra}

          {/* pre-wrap: these are pasted price lists whose line breaks carry the
              meaning. Highlighted, because the full message is where a bulk
              price list is actually read. */}
          <div className="trade-feed-modal__text">
            {item.message_text ? <HighlightMatch text={item.message_text} query={query} /> : "No text — attachment only"}
          </div>

          <dl className="trade-feed-modal__meta">
            <div>
              <dt>Sender</dt>
              <dd>
                {item.sender_country ? (
                  <span className="trade-feed-modal__flag">
                    <ReactCountryFlag countryCode={item.sender_country} svg aria-label={item.sender_country} />
                  </span>
                ) : null}
                {item.sender_name}
              </dd>
            </div>
            <div>
              <dt>Number</dt>
              <dd>{item.sender_phone || "—"}</dd>
            </div>
            <div>
              <dt>Group</dt>
              <dd>{item.group_name || "Direct message"}</dd>
            </div>
            <div>
              <dt>Received</dt>
              <dd>
                <span>
                  {time.absolute}
                  {time.relative ? <span className="trade-feed-modal__ago"> · {time.relative}</span> : null}
                </span>
              </dd>
            </div>
            {/* Which of our Cooby seats saw it — the same message often arrives
                on two seats, so this is how you tell them apart. */}
            {item.source ? (
              <div>
                <dt>Source</dt>
                <dd>{item.source}</dd>
              </div>
            ) : null}
            <div>
              <dt>Message ID</dt>
              {/* The join key for both badges (product_prices.listing_code,
                  requests.message_id), so a mismatch can be checked. */}
              <dd className="trade-feed-modal__id">{item.id}</dd>
            </div>
          </dl>

          <div className="trade-feed-modal__actions">
            <div className="trade-feed-modal__status">
              {item.badges?.trade_price_added ? <Badge tone="success" dot>Trade price added</Badge> : null}
              {item.badges?.lead_present ? <Badge tone="accent" dot>Lead in system</Badge> : null}
              {!item.badges?.trade_price_added && !item.badges?.lead_present ? (
                <Badge tone="muted">No price or lead yet</Badge>
              ) : null}
            </div>
            {waLink ? (
              // The same link and typed reply as the card's button.
              <a
                className="trade-feed-modal__wa"
                href={waLink.href}
                {...(waLink.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              >
                <Icon name="MessageCircle" size="sm" />
                Message on WhatsApp
              </a>
            ) : null}
          </div>

          {/* Every price the message carries, last, under a divider. Core List
              passes its own price strip (`extra`, above) and shows no table. */}
          {!extra && pricesOf(item).kind !== "none" && (
            <>
              <hr className="trade-feed-modal__divider" />
              <MessagePrices item={item} query={query} />
            </>
          )}
        </div>
      ) : null}
    </Modal>
  );
};

export default TradeFeedModal;
